import {
  Component,
  ElementRef,
  input,
  output,
  ViewChild,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  SimpleChanges,
  computed,
  inject
} from '@angular/core';
import { Observable, of } from 'rxjs';
import * as L from 'leaflet';
import { MappingPopupComponent } from './mapping-popup/mapping-popup.component';
import { MappingLot, Coordinates, ProjectsService } from '@zayan-erp/home-building';
import { LOT_STATUS_COLORS } from '../../constants/lot-status-colors';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/images/leaflet/marker-icon-2x.png',
  iconUrl: 'assets/images/leaflet/marker-icon.png',
  shadowUrl: 'assets/images/leaflet/marker-shadow.png'
});

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [MappingPopupComponent],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  @ViewChild(MappingPopupComponent) mappingPopup!: MappingPopupComponent;
  _projectService = inject(ProjectsService);
  imageUrl = input<string | null>(null);
  mapCoordinates = input<MappingLot[]>([]);
  markers = input<L.Marker[]>([]);
  zoomToMarker$ = input<Observable<any>>(of(null));
  zoomMarker = input<Coordinates | null>(null);
  zoomCounter = input<number>(0);
  enableMarkerClick = input<boolean>(true);
  enableMapClick = input<boolean>(true);
  mapContainerStyles = input<Record<string, string> | null>(null);
  fitImageToContainer = input<boolean>(false);

  onMarkerClick = output<L.Marker>();
  onMapClick = output<L.LatLng>();

  private map!: L.Map;
  private imageOverlay!: L.ImageOverlay;
  private markerLayer = L.layerGroup();
  private viewInitialized = false;
  showMappingPopup = false;
  clickedCoordinates: { x: number; y: number } | null = null;
  lotStatusColors = LOT_STATUS_COLORS;
  availableLots = computed(() => this.mapCoordinates().filter(lot => lot.lotCoordinateMapping === null));

  ngAfterViewInit() {
    this.viewInitialized = true;
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      this.initMapWithImage();
      this.listenToZoomToMarker();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only process changes after view is initialized
    if (!this.viewInitialized) {
      return;
    }

    if (changes['mapCoordinates']) {
      this.renderMarkers();
    }

    if (changes['imageUrl']) {
      this.renderImage();
    }

    if (changes['zoomMarker'] || changes['zoomCounter']) {
      const zoomMarker = this.zoomMarker();
      if (zoomMarker && zoomMarker.x !== null && zoomMarker.y !== null) {
        this.zoomToMarker(zoomMarker);
      }
    }
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  private initMapWithImage() {
    if (!this.mapContainer?.nativeElement) {
      return;
    }

    // Only initialize once
    if (this.map) {
      return;
    }

    try {
      // Use the ElementRef directly
      this.map = L.map(this.mapContainer.nativeElement, {
        zoomSnap: 0.25,
        zoomDelta: 0.25,
        crs: L.CRS.Simple,
        minZoom: -5,
        maxZoom: 10,
        zoomControl: false,
        wheelPxPerZoomLevel: 300
      });

      // Force map to recognize its container dimensions
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 100);

      this.map.on('click', (e: L.LeafletMouseEvent) => {
        if (this.enableMapClick()) {
          this.onMapClick.emit(e.latlng);
        }
        if (this.imageOverlay) {
          this.clickedCoordinates = {
            x: Math.round(e.latlng.lng),
            y: Math.round(e.latlng.lat)
          };

          if (this.enableMapClick()) {
            this.showMappingPopup = true;
          }
        }
      });
      this.markerLayer.addTo(this.map);

      // Only render image if URL exists at initialization time
      const imageUrl = this.imageUrl();
      if (imageUrl) {
        this.renderImage();
      }
    } catch (error) {
      console.error('Failed to initialize map:', error);
    }
  }

  private renderImage(retryCount = 0) {
    const imageUrl = this.imageUrl();
    if (!imageUrl) {
      if (this.imageOverlay && this.map) {
        this.map.removeLayer(this.imageOverlay);
        this.imageOverlay = undefined as any;
      }
      return;
    }

    // Ensure map is initialized before rendering image
    if (!this.map) {
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Double-check map still exists when image loads
      if (!this.map) {
        return;
      }

      // Force map to recalculate size before adding image
      this.map.invalidateSize();

      if (!this.fitImageToContainer()) {
        const bounds = L.latLngBounds([0, 0], [img.height, img.width]);
        if (this.imageOverlay) {
          this.map.removeLayer(this.imageOverlay);
        }
        this.imageOverlay = L.imageOverlay(imageUrl, bounds);
        this.imageOverlay.addTo(this.map);

        // Fit bounds once with proper options to prevent over-zooming
        this.map.fitBounds(bounds, {
          padding: [20, 20],
          maxZoom: 1 // Prevent zooming in too much initially
        });
      } else {
        // Get the container dimensions to make the image fill the entire visible area
        const container = this.mapContainer.nativeElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Use container dimensions as bounds so image fills the entire view
        const bounds = L.latLngBounds([0, 0], [containerHeight, containerWidth]);

        if (this.imageOverlay) {
          this.map.removeLayer(this.imageOverlay);
        }
        this.imageOverlay = L.imageOverlay(imageUrl, bounds);
        this.imageOverlay.addTo(this.map);

        this.map.fitBounds(bounds, {
          padding: [0, 0],
          animate: false
        });

        // Lock the view to prevent panning/zooming outside the image
        this.map.setMaxBounds(bounds);
      }

      // Additional invalidateSize call to handle async rendering
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 100);

      this.renderMarkers();
    };
    img.onerror = () => {
      if (retryCount < 3) {
        setTimeout(
          () => {
            this.renderImage(retryCount + 1);
          },
          1000 * (retryCount + 1)
        );
      }
    };
    img.src = imageUrl;
  }

  private renderMarkers() {
    if (!this.map) {
      return;
    }

    this.markerLayer.clearLayers();
    const coordinates = this.mapCoordinates();
    if (coordinates && coordinates.length > 0) {
      coordinates.forEach((lot: MappingLot) => {
        if (lot.lotCoordinateMapping && lot.lotCoordinateMapping.x !== null && lot.lotCoordinateMapping.y !== null) {
          const markerColor =
            lot.status === 'Open'
              ? this.lotStatusColors.Open
              : lot.status === 'Reserved'
                ? this.lotStatusColors.Reserved
                : lot.status === 'Sold'
                  ? this.lotStatusColors.Sold
                  : lot.status === 'Offer'
                    ? this.lotStatusColors.Offer
                    : lot.status === 'ScheduledReserved'
                      ? this.lotStatusColors.ScheduledReserved
                      : this.lotStatusColors.Open;

          const marker = L.marker([lot.lotCoordinateMapping.y!, lot.lotCoordinateMapping.x!], {
            title: `Click to view lot details.`,
            icon: L.divIcon({
              className: 'custom-marker',
              html: `
                <i class="bi bi-geo-alt-fill" style="
                  font-size: 28px;
                  color: ${markerColor};
                  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
                  display: block;
                "></i>
              `,
              iconSize: [28, 28],
              iconAnchor: [14, 28]
            })
          });
          marker.bindPopup(`
            <div class="d-flex gap-1 flex-column">
              <strong>Lot: ${lot.lotNumber}</strong>
              ${lot.scheduledAt ? `<small>Scheduled At: ${new Date(lot.scheduledAt).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</small>` : ''}
              ${lot.realtorFullName ? `<small>Realtor: ${lot.realtorFullName}</small>` : ''}
              ${lot.scheduleType ? `<small>Schedule Type: ${lot.scheduleType === 'InPerson' ? 'In Person' : 'Email'}</small>` : ''}
              ${lot.dealId ? `<a href="/sales/aps/${lot.dealId}/overview" target="_blank">View Deal</a>` : ''}
            </div>
          `);
          // this.attachPopupHover(marker);
          if (this.enableMarkerClick()) {
            marker.on('click', () => this.onMarkerClick.emit(marker));
          }
          marker.addTo(this.markerLayer);
        }
      });
    }

    this.markers().forEach(marker => {
      if (this.enableMarkerClick()) {
        marker.on('click', () => this.onMarkerClick.emit(marker));
      }
      marker.addTo(this.markerLayer);
    });
  }

  private listenToZoomToMarker() {
    this.zoomToMarker$().subscribe((lot: any) => {
      if (!lot?.lat || !lot?.lng) return;
      this.map.setView([lot.lat, lot.lng], this.map.getZoom() || 0);
    });
  }

  zoomIn() {
    if (this.map) {
      this.map.zoomIn();
    }
  }

  zoomOut() {
    if (this.map) {
      this.map.zoomOut();
    }
  }

  onPointSaved() {
    this.showMappingPopup = false;
    this.clickedCoordinates = null;
  }

  onPointCancelled() {
    this.showMappingPopup = false;
    this.clickedCoordinates = null;
  }

  zoomToMarker(coordinate: Coordinates | null) {
    if (!coordinate || coordinate.x === null || coordinate.y === null || !this.map) {
      return;
    }
    this.map.setView([coordinate.y, coordinate.x], 1);
  }

  private attachPopupHover(marker: L.Marker) {
    marker.on('mouseover', () => marker.openPopup());
    marker.on('mouseout', () => marker.closePopup());
  }
}
